import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router";
import {
  Profile,
  Briefcase,
  DocumentText1,
  FolderOpen,
  Calendar,
  Location,
  Sms,
  Call,
  User,
  CardPos,
  Bank,
  BookSaved,
  Heart,
  People,
  Edit2,
  Building,
  Global,
  Medal,
} from "iconsax-react";
import type { Icon } from "iconsax-react";
import cn from "classnames";
import { useAppSelector, useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { EmptyState, Loader } from "@/shared/components";
import { Avatar, Badge, StepsSwitcher, Button, Prompt, Toast } from "@/shared/ui";
import type { BadgeColor } from "@/shared/ui";
import { formatDateForDisplay, extractErrorMessage, formatMoneyKzt, PENDING_ACTIONS_PAGE_ROUTE } from "@/shared/utils";
import { STATUS_MAP } from "@/features/hr/employees/consts/statuses";
import {
  buildSections,
  useCompletePersonalSheetMutation,
  useUploadPersonalSheetDraftFileMutation,
  useAutosavePersonalSheetDraftMutation,
  useGetPersonalSheetDraftQuery,
} from "@/features/self/personal-sheet";
import type { DraftSectionKey, PersonalSheetCompleteness } from "@/features/self/personal-sheet";
import { CONTRACTOR_STATUS_MAP } from "../consts/contractorStatuses";
import { isFioChangeMissingIdFile } from "../utils/personalSheetValidation";
import { deriveCompleteness } from "../utils/completeness";
import { enrichDraftFilesFromStaged } from "../utils/stagedFiles";
import { useGetSelfProfileQuery } from "../api";
import {
  useCreateDataChangeRequestMutation,
  useGetSelfRequestsQuery,
  useGetSelfDataChangeDraftQuery,
  useUpsertSelfDataChangeDraftMutation,
  useDeleteSelfDataChangeDraftMutation,
  useUploadSelfDataChangeDraftFileMutation,
} from "../../requests/api";
import { buildMergedSheet } from "../utils/buildMergedSheet";
import { getActiveDataChangeRequest } from "../../requests/utils/requestRestrictions";
import type { PersonalSheet } from "../types/personalSheet";
import type { WorkerProfile, SelfServiceProfile, VolunteerProfile } from "../../shared/types";
import type { ManagerProfile, ContractorProfile, MsbContractorProfile } from "../types";
import {
  ProfilePageSkeleton,
  VolunteerProfileSkeleton,
  MsbProfileSkeleton,
  ProfileDataRow,
  PersonalInfoSection,
  ContractInfoSection,
  DocumentsSection,
} from "../components";
import {
  GeneralSection,
  ContactsSection,
  AddressesSection,
  EmergencyContactsSection,
  IdDocumentsSection,
  BankingSection,
  EducationSection,
  ExperienceSection,
  SocialCategoriesSection,
  LanguagesSection,
  ScientificWorksSection,
  GovernmentAwardsSection,
} from "../components/personal-sheet";
import {
  EditGeneralSection,
  EditContactsSection,
  EditAddressesSection,
  EditEmergencyContactsSection,
  EditIdDocumentsSection,
  EditBankingSection,
  EditEducationSection,
  EditExperienceSection,
  EditSocialCategoriesSection,
  EditLanguagesSection,
  EditScientificWorksSection,
  EditGovernmentAwardsSection,
} from "../components/personal-sheet/edit";
import type { SectionFormHandle } from "../components/personal-sheet/edit";
import ConfirmChangesModal from "../components/personal-sheet/ConfirmChangesModal";

type ProfileTabId = "personal_sheet" | "work_info" | "documents";
type PersonalSheetSectionId =
  | "general"
  | "contacts"
  | "addresses"
  | "emergency"
  | "id_documents"
  | "banking"
  | "education"
  | "experience"
  | "social_categories"
  | "languages"
  | "scientific_works"
  | "government_awards";

const PERSONAL_SHEET_SECTIONS: { id: PersonalSheetSectionId; icon: Icon; labelKey: string }[] = [
  { id: "general", icon: User, labelKey: "personalSheet.sections.general" },
  { id: "contacts", icon: Call, labelKey: "personalSheet.sections.contacts" },
  { id: "addresses", icon: Location, labelKey: "personalSheet.sections.addresses" },
  { id: "emergency", icon: People, labelKey: "personalSheet.sections.emergency" },
  { id: "id_documents", icon: CardPos, labelKey: "personalSheet.sections.idDocuments" },
  { id: "banking", icon: Bank, labelKey: "personalSheet.sections.banking" },
  { id: "education", icon: BookSaved, labelKey: "personalSheet.sections.education" },
  { id: "experience", icon: Briefcase, labelKey: "personalSheet.sections.experience" },
  { id: "languages", icon: Global, labelKey: "personalSheet.sections.languages" },
  { id: "scientific_works", icon: DocumentText1, labelKey: "personalSheet.sections.scientificWorks" },
  { id: "government_awards", icon: Medal, labelKey: "personalSheet.sections.governmentAwards" },
  { id: "social_categories", icon: Heart, labelKey: "personalSheet.sections.socialCategories" },
];

const CONTRACTOR_SECTION_CONFIG: { id: "personal_info" | "contract_info" | "documents"; icon: Icon; labelKey: string }[] = [
  { id: "personal_info", icon: Profile, labelKey: "overview.personalInfo.title" },
  { id: "contract_info", icon: DocumentText1, labelKey: "overview.contractInfo.title" },
  { id: "documents", icon: FolderOpen, labelKey: "overview.documents.title" },
];

function getJobPosition(profile: SelfServiceProfile, locale: string): string {
  if (profile.user_type === "volunteer") return "";
  if (profile.user_type === "worker") {
    const p = profile as WorkerProfile;
    if (locale === "kk") return p.job_position_kk || p.job_position_ru || "";
    return p.job_position_ru || p.job_position_kk || "";
  }
  if (profile.user_type === "manager") {
    const p = profile as ManagerProfile;
    if (locale === "kk") return p.job_position_kk || p.job_position_ru || "";
    return p.job_position_ru || p.job_position_kk || "";
  }
  return "";
}

const EMPTY_PERSONAL_SHEET: PersonalSheet = {
  general: {
    iin: "",
    surname: "",
    name: "",
    father_name: "",
    date_of_birth: "",
    gender: "",
    family_status: "",
    nationality: "",
    citizenship: "",
    is_resident: true,
    is_student: false,
    enrollment_start_date: null,
    enrollment_end_date: null,
    city_of_birth: null,
    city_of_birth_other: null,
    photo_url: null,
    has_no_education: false,
    has_no_work_experience: false,
  },
  contacts: null,
  addresses: null,
  emergency_contacts: [],
  id_documents: null,
  banking: null,
  kyc_documents: null,
  education: [],
  experience: [],
  social_categories: [],
  languages: [],
  scientific_works: [],
  government_awards: [],
};

export default function ProfilePage() {
  const { t, i18n } = useTranslation("EmployeePortal");
  const { user, hasPermission } = useAuth();
  const canRequestDataChange = hasPermission(PERMISSIONS.SELF.REQUEST_DATA_CHANGE);
  const navigate = useNavigate();
  const { data: profile, isLoading, error } = useGetSelfProfileQuery();
  const mustChangePassword = useAppSelector((state) => state.auth.mustChangePassword);
  const [searchParams, setSearchParams] = useSearchParams();

  const isCompletionMode =
    !!profile &&
    (profile.user_type === "worker" || profile.user_type === "manager") &&
    (profile as WorkerProfile).personal_sheet_status === "pending_completion";

  const [activeTab, setActiveTab] = useState<ProfileTabId>("personal_sheet");
  const [activeSection, setActiveSection] = useState<PersonalSheetSectionId>("general");
  const [contractorSection, setContractorSection] = useState<"personal_info" | "contract_info" | "documents">("personal_info");

  const [isEditMode, setIsEditMode] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompletingSheet, setIsCompletingSheet] = useState(false);
  const [errorSections, setErrorSections] = useState<PersonalSheetSectionId[]>([]);
  const [completeness, setCompleteness] = useState<PersonalSheetCompleteness | null>(null);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [draftSavedVisible, setDraftSavedVisible] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<{ changes: Record<string, unknown>; files: Record<string, File> } | null>(null);

  const [createDataChangeRequest] = useCreateDataChangeRequestMutation();
  const [completePersonalSheet] = useCompletePersonalSheetMutation();
  const [uploadPersonalSheetDraftFile] = useUploadPersonalSheetDraftFileMutation();
  const [autosaveDraft, { isLoading: isSavingDraft }] = useAutosavePersonalSheetDraftMutation();
  const { data: draftResponse } = useGetPersonalSheetDraftQuery(undefined, { skip: !isCompletionMode });
  const { data: selfRequests } = useGetSelfRequestsQuery(undefined, { skip: !isEditMode && searchParams.get("edit") !== "true" });

  const {
    data: editDraftResponse,
    isLoading: isEditDraftLoading,
    isError: isEditDraftError,
  } = useGetSelfDataChangeDraftQuery(undefined, { skip: !isEditMode || isCompletionMode });
  const [upsertSelfDataChangeDraft] = useUpsertSelfDataChangeDraftMutation();
  const [deleteSelfDataChangeDraft] = useDeleteSelfDataChangeDraftMutation();
  const [uploadSelfDataChangeDraftFile] = useUploadSelfDataChangeDraftFileMutation();
  const [editDraftToast, setEditDraftToast] = useState<"saved" | "error" | null>(null);
  const editDraftRestoredRef = useRef(false);

  const enrichedDraftSheet = useMemo(
    function enrichedDraftSheet() {
      if (!draftResponse) return null;
      return enrichDraftFilesFromStaged(draftResponse.data, draftResponse.uploaded_files);
    },
    [draftResponse],
  );

  const editDraftSheet = useMemo(
    function editDraftSheet() {
      if (!editDraftResponse?.has_draft) return null;
      return enrichDraftFilesFromStaged(editDraftResponse.data, editDraftResponse.uploaded_files);
    },
    [editDraftResponse],
  );

  const generalRef = useRef<SectionFormHandle>(null);
  const contactsRef = useRef<SectionFormHandle>(null);
  const addressesRef = useRef<SectionFormHandle>(null);
  const emergencyRef = useRef<SectionFormHandle>(null);
  const idDocumentsRef = useRef<SectionFormHandle>(null);
  const bankingRef = useRef<SectionFormHandle>(null);
  const educationRef = useRef<SectionFormHandle>(null);
  const experienceRef = useRef<SectionFormHandle>(null);
  const socialCategoriesRef = useRef<SectionFormHandle>(null);
  const languagesRef = useRef<SectionFormHandle>(null);
  const scientificWorksRef = useRef<SectionFormHandle>(null);
  const governmentAwardsRef = useRef<SectionFormHandle>(null);

  const allRefs = [generalRef, contactsRef, addressesRef, emergencyRef, idDocumentsRef, bankingRef, educationRef, experienceRef, languagesRef, scientificWorksRef, governmentAwardsRef, socialCategoriesRef];

  const SECTION_ORDER: PersonalSheetSectionId[] = ["general", "contacts", "addresses", "emergency", "id_documents", "banking", "education", "experience", "languages", "scientific_works", "government_awards", "social_categories"];

  const buildMergedCompletionSheet = useCallback(function buildMergedCompletionSheet(): PersonalSheet {
    const sheet = (isCompletionMode ? enrichedDraftSheet : (profile as WorkerProfile | null)?.personal_sheet) ?? EMPTY_PERSONAL_SHEET;
    return buildMergedSheet(sheet, {
      generalRef,
      contactsRef,
      addressesRef,
      emergencyRef,
      idDocumentsRef,
      bankingRef,
      educationRef,
      experienceRef,
      languagesRef,
      scientificWorksRef,
      governmentAwardsRef,
      socialCategoriesRef,
    });
  }, [profile, isCompletionMode, enrichedDraftSheet]);

  function handleSectionChange(stepId: PersonalSheetSectionId) {
    setActiveSection(stepId);
    if (isCompletionMode && isAnyFormDirty()) {
      persistDraftAndGetCompleteness()
        .then((updated) => {
          if (updated) setCompleteness(updated);
        })
        .catch((err: unknown) => {
          setPrompt({
            title: t("personalSheet.completion.errorTitle"),
            text: extractErrorMessage(err, "personalSheet.completion.errorText", t),
            variant: "error",
          });
        });
    } else if (isEditMode && !isCompletionMode && isAnyFormDirty()) {
      void persistEditDraft(stepId);
    }
  }

  async function persistEditDraft(section: PersonalSheetSectionId) {
    if (isEditDraftError) return;
    const draftFiles = allRefs.reduce<Record<string, File>>((acc, ref) => {
      const files = ref.current?.getFiles();
      return files ? { ...acc, ...files } : acc;
    }, {});
    try {
      await Promise.all(
        Object.entries(draftFiles).map(([fieldName, file]) => {
          const formData = new FormData();
          formData.append("field_name", fieldName);
          formData.append("file", file);
          return uploadSelfDataChangeDraftFile(formData).unwrap();
        }),
      );
      await upsertSelfDataChangeDraft({
        data: buildMergedCompletionSheet(),
        last_active_section: toDraftSectionKey(section),
      }).unwrap();
      setEditDraftToast("saved");
    } catch {
      setEditDraftToast("error");
    }
  }

  function isAnyFormDirty(): boolean {
    return allRefs.some((ref) => ref.current?.isDirty());
  }

  function handleEnterEditMode() {
    const activeRequest = getActiveDataChangeRequest(selfRequests);
    if (activeRequest) {
      setToast(t("personalSheet.edit.activeRequestBlocks"));
      return;
    }
    setIsEditMode(true);
    setActiveTab("personal_sheet");
  }

  const handleExitEditMode = useCallback(function handleExitEditMode() {
    setIsEditMode(false);
    setPendingChanges(null);
    setShowConfirmModal(false);
    setToast(null);
    editDraftRestoredRef.current = false;
  }, []);

  useEffect(
    function restoreEditDraftSection() {
      if (!isEditMode || isCompletionMode) return;
      if (editDraftRestoredRef.current || isEditDraftLoading) return;
      editDraftRestoredRef.current = true;
      if (editDraftResponse?.has_draft && editDraftResponse.last_active_section) {
        setActiveSection(fromDraftSectionKey(editDraftResponse.last_active_section));
      }
    },
    [editDraftResponse, isEditDraftLoading, isEditMode, isCompletionMode],
  );

  async function handleSubmitChanges() {
    const SECTION_API_NAMES = ["general", "contacts", "addresses", "emergency_contacts", "id_documents", "banking", "education", "experience", "languages", "scientific_works", "government_awards", "social_categories"];

    const validations = await Promise.all(
      allRefs.map((ref) => {
        if (!ref.current?.isDirty()) return Promise.resolve(true);
        return ref.current.validate();
      }),
    );
    const firstErrorIdx = validations.findIndex((v) => !v);
    if (firstErrorIdx !== -1) {
      setActiveSection(SECTION_ORDER[firstErrorIdx]);
      return;
    }

    const allChanges: Record<string, unknown> = {};
    const allFiles: Record<string, File> = {};

    allRefs.forEach((ref, idx) => {
      const changes = ref.current?.getChanges();
      const files = ref.current?.getFiles();
      if (changes && Object.keys(changes).length > 0) {
        allChanges[SECTION_API_NAMES[idx]] = changes;
      }
      if (files) {
        Object.assign(allFiles, files);
      }
    });

    for (const fileKey of Object.keys(allFiles)) {
      const sectionName = fileKey.split(".")[0];
      if (!allChanges[sectionName]) {
        allChanges[sectionName] = {};
      }
    }

    for (const ref of allRefs) {
      const flags = ref.current?.getFlagChanges?.();
      if (flags) {
        if (!allChanges["general"]) allChanges["general"] = {};
        Object.assign(allChanges["general"] as Record<string, unknown>, flags);
      }
    }

    if (Object.keys(allChanges).length === 0 && Object.keys(allFiles).length === 0) {
      setToast(t("personalSheet.edit.noChanges"));
      return;
    }

    if (isFioChangeMissingIdFile(allChanges, allFiles)) {
      setActiveSection("id_documents");
      setToast(t("personalSheet.edit.fioRequiresIdFile"));
      return;
    }

    setPendingChanges({ changes: allChanges, files: allFiles });
    setShowConfirmModal(true);
  }

  async function handleConfirmSubmit() {
    if (!pendingChanges) return;

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("sections", JSON.stringify(pendingChanges.changes));

      for (const [key, file] of Object.entries(pendingChanges.files)) {
        formData.append(key, file);
      }

      await createDataChangeRequest(formData).unwrap();
      void deleteSelfDataChangeDraft();
      editDraftRestoredRef.current = false;
      setShowConfirmModal(false);
      setPendingChanges(null);
      setIsEditMode(false);
      setToast(null);
      setPrompt({
        title: t("personalSheet.edit.successTitle"),
        text: t("personalSheet.edit.successText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setShowConfirmModal(false);
      setPrompt({
        title: t("personalSheet.edit.errorTitle"),
        text: extractErrorMessage(err, "personalSheet.edit.errorText", t),
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCompleteSheet() {
    if (isCompletingSheet) return;

    const validations = await Promise.all(allRefs.map((ref) => ref.current?.validate() ?? Promise.resolve(true)));
    const newErrorSections: PersonalSheetSectionId[] = [];
    validations.forEach((valid, idx) => {
      if (!valid) newErrorSections.push(SECTION_ORDER[idx]);
    });
    setErrorSections(newErrorSections);
    if (newErrorSections.length > 0) {
      setActiveSection(newErrorSections[0]);
      const incompleteSections = newErrorSections
        .map(function toSectionLabel(sectionId) {
          const section = PERSONAL_SHEET_SECTIONS.find((s) => s.id === sectionId);
          return section ? t(section.labelKey) : "";
        })
        .filter((label) => label !== "")
        .join(", ");
      setToast(t("personalSheet.edit.validationError", { sections: incompleteSections }));
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setErrorSections([]);

    const completionFiles = allRefs.reduce<Record<string, File>>((acc, ref) => {
      const files = ref.current?.getFiles();
      return files ? { ...acc, ...files } : acc;
    }, {});
    const sections = buildSections(buildMergedCompletionSheet());
    try {
      setIsCompletingSheet(true);
      await Promise.all(
        Object.entries(completionFiles).map(([fieldName, file]) => {
          const formData = new FormData();
          formData.append("field_name", fieldName);
          formData.append("file", file);
          return uploadPersonalSheetDraftFile(formData).unwrap();
        }),
      );
      await completePersonalSheet({ sections }).unwrap();
      navigate(PENDING_ACTIONS_PAGE_ROUTE);
    } catch (err: unknown) {
      setPrompt({
        title: t("personalSheet.completion.errorTitle"),
        text: extractErrorMessage(err, "personalSheet.completion.errorText", t),
        variant: "error",
      });
    } finally {
      setIsCompletingSheet(false);
    }
  }

  function toDraftSectionKey(section: PersonalSheetSectionId): DraftSectionKey {
    return (section === "emergency" ? "emergency_contacts" : section) as DraftSectionKey;
  }

  function fromDraftSectionKey(section: DraftSectionKey): PersonalSheetSectionId {
    return (section === "emergency_contacts" ? "emergency" : section) as PersonalSheetSectionId;
  }

  async function persistDraftAndGetCompleteness(): Promise<PersonalSheetCompleteness | null> {
    const draftFiles = allRefs.reduce<Record<string, File>>((acc, ref) => {
      const files = ref.current?.getFiles();
      return files ? { ...acc, ...files } : acc;
    }, {});
    await Promise.all(
      Object.entries(draftFiles).map(([fieldName, file]) => {
        const formData = new FormData();
        formData.append("field_name", fieldName);
        formData.append("file", file);
        return uploadPersonalSheetDraftFile(formData).unwrap();
      }),
    );
    const res = await autosaveDraft({ data: buildMergedCompletionSheet(), last_active_section: toDraftSectionKey(activeSection) }).unwrap();
    return res.completeness ?? null;
  }

  async function handleSaveDraft() {
    if (isSavingDraft || isCompletingSheet) return;
    try {
      const updated = await persistDraftAndGetCompleteness();
      if (updated) setCompleteness(updated);
      setDraftSavedVisible(true);
    } catch (err: unknown) {
      setPrompt({
        title: t("personalSheet.completion.errorTitle"),
        text: extractErrorMessage(err, "personalSheet.completion.errorText", t),
        variant: "error",
      });
    }
  }

  useEffect(() => {
    if (!profile) return;
    if (profile.user_type === "worker" || profile.user_type === "manager") {
      const hasPersonalSheet = (profile as WorkerProfile).personal_sheet != null;
      if (!hasPersonalSheet && activeTab === "personal_sheet") {
        setActiveTab("work_info");
      }
    }
  }, [profile, activeTab]);

  useEffect(() => {
    if (isCompletionMode && !isEditMode) {
      setIsEditMode(true);
      setActiveTab("personal_sheet");
    }
  }, [isCompletionMode, isEditMode]);

  useEffect(() => {
    if (searchParams.get("edit") === "true" && profile && (profile.user_type === "worker" || profile.user_type === "manager")) {
      handleEnterEditMode();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, searchParams]);

  useEffect(() => {
    if (!isEditMode || isCompletionMode) return;
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (isAnyFormDirty()) {
        e.preventDefault();
      }
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isCompletionMode]);

  useEffect(() => {
    if (isCompletionMode && draftResponse?.completeness) {
      setCompleteness(draftResponse.completeness);
    }
  }, [isCompletionMode, draftResponse]);

  if (isLoading) {
    if (user?.user_type === "volunteer") {
      return (
        <>
          <title>{t("meta.profileTitle")}</title>
          <meta name="description" content={t("meta.description")} />
          <section className="flex-1 min-w-0">
            <VolunteerProfileSkeleton />
          </section>
        </>
      );
    }

    if (user?.user_type === "msb_contractor") {
      return (
        <>
          <title>{t("meta.profileTitle")}</title>
          <meta name="description" content={t("meta.description")} />
          <section className="flex-1 min-w-0">
            <MsbProfileSkeleton />
          </section>
        </>
      );
    }

    return (
      <>
        <title>{t("meta.profileTitle")}</title>
        <meta name="description" content={t("meta.description")} />
        <section className="flex-1 min-w-0">
          <ProfilePageSkeleton />
        </section>
      </>
    );
  }

  if (error || !profile) {
    const isNotFound =
      error && typeof error === "object" && "status" in error && (error as { status?: number }).status === 404;

    return (
      <>
        <title>{t("meta.profileTitle")}</title>
        <meta name="description" content={t("meta.description")} />
        <section className="flex-1 min-w-0">
          <div className="flex items-center justify-between pb-4 border-b surface-base-stroke mb-5 pt-1">
            <h1 className="text-display-xs content-base-primary">{t("overview.accountTitle")}</h1>
          </div>
          <div className="radius-3xl surface-base-fill p-7">
            <EmptyState
              noDataText={isNotFound ? t("overview.error.notFound") : t("overview.error.default")}
              noDataHint={t("overview.error.hint")}
            />
          </div>
        </section>
      </>
    );
  }

  if (profile.user_type === "volunteer") {
    const volunteer = profile as VolunteerProfile;
    const locale = i18n.language;
    const regionName = volunteer.region
      ? locale === "kk"
        ? volunteer.region.name_kk
        : volunteer.region.name_ru
      : null;

    return (
      <>
        <title>{t("meta.profileTitle")}</title>
        <meta name="description" content={t("meta.description")} />
        <section className="flex-1 min-w-0 flex flex-col gap-6">
          <div className="p-7 radius-3xl surface-base-fill flex items-center gap-4">
            <Avatar name={volunteer.full_name} size={64} textClassName="text-display-2xs" />
            <h1 className="text-heading-lg content-base-primary">{volunteer.full_name}</h1>
          </div>

          <div className="p-7 radius-3xl surface-base-fill">
            <h2 className="text-display-2xs content-base-primary mb-7">{t("overview.personalInfo.panelTitle")}</h2>
            <div className="flex flex-col">
              <ProfileDataRow
                icon={Calendar}
                label={t("overview.personalInfo.dateOfBirth")}
                value={formatDateForDisplay(volunteer.date_of_birth)}
              />
              <ProfileDataRow icon={Location} label={t("overview.personalInfo.region")} value={regionName} />
              <ProfileDataRow icon={Sms} label={t("overview.personalInfo.email")} value={volunteer.email || ""} />
              <ProfileDataRow icon={Call} label={t("overview.personalInfo.phone")} value={volunteer.phone || ""} />
            </div>
          </div>
        </section>
      </>
    );
  }

  if (profile.user_type === "msb_contractor") {
    const msb = profile as MsbContractorProfile;
    const locale = (i18n.language as "ru" | "kk" | "en") || "ru";

    return (
      <>
        <title>{t("meta.profileTitle")}</title>
        <meta name="description" content={t("meta.description")} />
        <section className="flex-1 min-w-0 flex flex-col gap-6">
          <div className="p-7 radius-3xl surface-base-fill flex items-center gap-4">
            <div className="flex items-center justify-center shrink-0 surface-brand-subtle content-action-brand radius-full" style={{ width: 64, height: 64 }}>
              <Building size={32} color="currentColor" />
            </div>
            <div>
              <h1 className="text-heading-lg content-base-primary">{msb.org_name}</h1>
              <p className="text-body-regular-sm content-action-neutral">БИН: {msb.bin}</p>
            </div>
          </div>

          <div className="p-7 radius-3xl surface-base-fill">
            <h2 className="text-display-2xs content-base-primary mb-7">{t("overview.personalInfo.panelTitle")}</h2>
            <div className="flex flex-col">
              <ProfileDataRow icon={Profile} label={t("overview.personalInfo.representative")} value={msb.representative} />
              <ProfileDataRow icon={Sms} label={t("overview.personalInfo.email")} value={msb.email || ""} />
              <ProfileDataRow icon={Call} label={t("overview.personalInfo.phone")} value={msb.phone || ""} />
            </div>
          </div>

          <div className="p-7 radius-3xl surface-base-fill">
            <h2 className="text-display-2xs content-base-primary mb-7">{t("overview.contractInfo.title")}</h2>
            <div className="flex flex-col">
              <ProfileDataRow icon={Briefcase} label={t("overview.contractInfo.serviceName")} value={msb.services_subject} />
              <ProfileDataRow
                icon={Calendar}
                label={t("overview.contractInfo.contractDate")}
                value={formatDateForDisplay(msb.contract_date)}
              />
              {msb.contract_end_date && (
                <ProfileDataRow
                  icon={Calendar}
                  label={t("overview.contractInfo.contractEndDate")}
                  value={formatDateForDisplay(msb.contract_end_date)}
                />
              )}
              <ProfileDataRow
                icon={Briefcase}
                label={t("overview.contractInfo.contractAmount")}
                value={formatMoneyKzt(parseFloat(msb.contract_amount || "0"), locale)}
              />
            </div>
          </div>
        </section>
      </>
    );
  }

  if (profile.user_type === "contractor") {
    const contractor = profile as ContractorProfile;
    const contractorStatusConfig = CONTRACTOR_STATUS_MAP[contractor.status];

    function renderContractorContent() {
      switch (contractorSection) {
        case "personal_info":
          return <PersonalInfoSection profile={contractor} />;
        case "contract_info":
          return <ContractInfoSection profile={contractor} mustChangePassword={mustChangePassword} />;
        case "documents":
          return <DocumentsSection profile={contractor} />;
        default:
          return null;
      }
    }

    return (
      <>
        <title>{t("meta.profileTitle")}</title>
        <meta name="description" content={t("meta.description")} />
        <section className="flex-1 min-w-0 flex flex-col gap-6">
          <div className="p-7 radius-3xl surface-base-fill flex items-center gap-4">
            <Avatar name={contractor.full_name} size={64} textClassName="text-display-2xs" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <h1 className="text-heading-lg content-base-primary">{contractor.full_name}</h1>
                {contractorStatusConfig && (
                  <Badge
                    variant="soft"
                    color={contractorStatusConfig.color}
                    text={t(contractorStatusConfig.label)}
                    icon={contractorStatusConfig.icon}
                  />
                )}
              </div>
              <span className="text-body-regular-sm content-base-secondary">{contractor.iin}</span>
            </div>
          </div>

          <div className="p-6 radius-3xl surface-base-fill flex flex-wrap items-center gap-2">
            {CONTRACTOR_SECTION_CONFIG.map(function renderContractorTab(tab) {
              const TabIcon = tab.icon;
              const isActive = contractorSection === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setContractorSection(tab.id)}
                  className={cn(
                    "px-3 py-2.5 flex justify-start items-center gap-2 radius-sm text-label-md cursor-pointer",
                    isActive ? "background-brand-fill text-white" : "surface-component-fill content-action-neutral",
                  )}>
                  <TabIcon size={20} color="currentColor" />
                  {t(tab.labelKey)}
                </button>
              );
            })}
          </div>

          <div className="radius-3xl surface-base-fill p-7">{renderContractorContent()}</div>
        </section>
      </>
    );
  }

  const workerOrManager = profile as WorkerProfile | ManagerProfile;
  const locale = i18n.language as "ru" | "kk" | "en";
  const jobPosition = getJobPosition(profile, locale);
  const hasPersonalSheet = workerOrManager.personal_sheet != null;
  const statusConfig = STATUS_MAP[workerOrManager.status ?? "active"];

  const derivedCompleteness = deriveCompleteness(completeness, PERSONAL_SHEET_SECTIONS.length);
  const completedSections = derivedCompleteness.completedSections as PersonalSheetSectionId[];
  const completedCount = derivedCompleteness.completedCount;
  const totalSections = derivedCompleteness.totalSections;
  const progressPercent = derivedCompleteness.progressPercent;
  const allSectionsComplete = derivedCompleteness.allSectionsComplete;

  const tabs: { id: ProfileTabId; label: string; icon: Icon }[] = [];
  if (hasPersonalSheet) {
    tabs.push({ id: "personal_sheet", label: t("personalSheet.tabs.personalSheet"), icon: Profile });
  }
  tabs.push({ id: "work_info", label: t("personalSheet.tabs.workInfo"), icon: Briefcase });
  tabs.push({ id: "documents", label: t("personalSheet.tabs.documents"), icon: FolderOpen });

  const personalSheetSteps = PERSONAL_SHEET_SECTIONS.map((s) => ({
    id: s.id,
    label: t(s.labelKey),
    icon: s.icon,
  }));

  function renderPersonalSheetContent() {
    const sheet = isCompletionMode ? enrichedDraftSheet : workerOrManager.personal_sheet;
    if (!sheet) {
      return isCompletionMode ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : null;
    }

    if (isEditMode) {
      if (!isCompletionMode && isEditDraftLoading) {
        return (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        );
      }
      return (
        <div>
          <div hidden={activeSection !== "general"}>
            <EditGeneralSection ref={generalRef} general={sheet.general} draftValues={editDraftSheet?.general} />
          </div>
          <div hidden={activeSection !== "contacts"}>
            <EditContactsSection ref={contactsRef} contacts={sheet.contacts} draftValues={editDraftSheet?.contacts} />
          </div>
          <div hidden={activeSection !== "addresses"}>
            <EditAddressesSection ref={addressesRef} addresses={sheet.addresses} draftValues={editDraftSheet?.addresses} />
          </div>
          <div hidden={activeSection !== "emergency"}>
            <EditEmergencyContactsSection ref={emergencyRef} contacts={sheet.emergency_contacts} recordsEditable={isCompletionMode} draftAddedRecords={editDraftSheet ? editDraftSheet.emergency_contacts.slice(sheet.emergency_contacts.length) : undefined} />
          </div>
          <div hidden={activeSection !== "id_documents"}>
            <EditIdDocumentsSection
              ref={idDocumentsRef}
              idDocuments={sheet.id_documents}
              gender={sheet.general.gender}
              isResident={sheet.general.is_resident}
              draftValues={editDraftSheet?.id_documents}
            />
          </div>
          <div hidden={activeSection !== "banking"}>
            <EditBankingSection ref={bankingRef} banking={sheet.banking} draftValues={editDraftSheet?.banking} />
          </div>
          <div hidden={activeSection !== "education"}>
            <EditEducationSection ref={educationRef} records={sheet.education} initialHasNoEducation={sheet.general.has_no_education ?? false} recordsEditable={isCompletionMode} draftAddedRecords={editDraftSheet ? editDraftSheet.education.slice(sheet.education.length) : undefined} />
          </div>
          <div hidden={activeSection !== "experience"}>
            <EditExperienceSection ref={experienceRef} records={sheet.experience} initialHasNoExperience={sheet.general.has_no_work_experience ?? false} recordsEditable={isCompletionMode} workProofUrl={sheet.kyc_documents?.work_proof_file_url ?? null} enpfUrl={sheet.kyc_documents?.enpf_certificate_file_url ?? null} sheetIin={sheet.general.iin} draftAddedRecords={editDraftSheet ? editDraftSheet.experience.slice(sheet.experience.length) : undefined} />
          </div>
          <div hidden={activeSection !== "languages"}>
            <EditLanguagesSection ref={languagesRef} records={sheet.languages} recordsEditable={isCompletionMode} draftAddedRecords={editDraftSheet ? editDraftSheet.languages.slice(sheet.languages.length) : undefined} />
          </div>
          <div hidden={activeSection !== "scientific_works"}>
            <EditScientificWorksSection ref={scientificWorksRef} records={sheet.scientific_works} initialHasNoScientificWorks={sheet.general.has_no_scientific_works ?? false} recordsEditable={isCompletionMode} draftAddedRecords={editDraftSheet ? editDraftSheet.scientific_works.slice(sheet.scientific_works.length) : undefined} />
          </div>
          <div hidden={activeSection !== "government_awards"}>
            <EditGovernmentAwardsSection ref={governmentAwardsRef} records={sheet.government_awards} initialHasNoGovernmentAwards={sheet.general.has_no_government_awards ?? false} recordsEditable={isCompletionMode} draftAddedRecords={editDraftSheet ? editDraftSheet.government_awards.slice(sheet.government_awards.length) : undefined} />
          </div>
          <div hidden={activeSection !== "social_categories"}>
            <EditSocialCategoriesSection ref={socialCategoriesRef} records={sheet.social_categories} initialHasNoSocialCategories={sheet.general.has_no_social_categories ?? false} recordsEditable={isCompletionMode} draftAddedRecords={editDraftSheet ? editDraftSheet.social_categories.slice(sheet.social_categories.length) : undefined} />
          </div>
        </div>
      );
    }

    switch (activeSection) {
      case "general":
        return (
          <GeneralSection
            general={sheet.general}
            enrollmentVerificationUrl={sheet.kyc_documents?.enrollment_verification_file_url ?? null}
            workerAgreementUrl={sheet.kyc_documents?.worker_agreement_file_url ?? null}
          />
        );
      case "contacts":
        return <ContactsSection contacts={sheet.contacts} />;
      case "addresses":
        return <AddressesSection addresses={sheet.addresses} />;
      case "emergency":
        return <EmergencyContactsSection contacts={sheet.emergency_contacts} />;
      case "id_documents":
        return <IdDocumentsSection idDocuments={sheet.id_documents} />;
      case "banking":
        return <BankingSection banking={sheet.banking} />;
      case "education":
        return <EducationSection records={sheet.education} hasNoEducation={sheet.general.has_no_education} />;
      case "experience":
        return <ExperienceSection records={sheet.experience} hasNoExperience={sheet.general.has_no_work_experience} workProofUrl={sheet.kyc_documents?.work_proof_file_url ?? null} enpfUrl={sheet.kyc_documents?.enpf_certificate_file_url ?? null} />;
      case "languages":
        return <LanguagesSection records={sheet.languages} />;
      case "scientific_works":
        return <ScientificWorksSection records={sheet.scientific_works} hasNoScientificWorks={sheet.general.has_no_scientific_works} />;
      case "government_awards":
        return <GovernmentAwardsSection records={sheet.government_awards} hasNoGovernmentAwards={sheet.general.has_no_government_awards} />;
      case "social_categories":
        return <SocialCategoriesSection records={sheet.social_categories} hasNoSocialCategories={sheet.general.has_no_social_categories} />;
      default:
        return null;
    }
  }

  function renderTabContent() {
    switch (activeTab) {
      case "personal_sheet":
        return (
          <div className="radius-3xl surface-base-fill p-7">
            {toast && (
              <div className="mb-5">
                <Toast color="notice" text={toast} isFullWidth autoClose={false} onClose={() => setToast(null)} />
              </div>
            )}
            {editDraftToast && (
              <div className="fixed bottom-6 right-6 z-50">
                <Toast
                  key={editDraftToast}
                  color={editDraftToast === "saved" ? "positive" : "negative"}
                  text={editDraftToast === "saved" ? t("personalSheet.edit.draftSaved") : t("personalSheet.edit.draftSaveFailed")}
                  autoCloseDuration={5000}
                  onClose={() => setEditDraftToast(null)}
                />
              </div>
            )}
            {isCompletionMode && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-label-sm content-base-secondary">
                    {t("personalSheet.completion.progressLabel", { completed: completedCount, total: totalSections })}
                  </span>
                  <span className="text-label-sm content-base-primary">{progressPercent}%</span>
                </div>
                <div className="w-full h-2 surface-secondary-fill radius-full overflow-hidden">
                  <div className="h-full background-brand-fill radius-full transition-all duration-500" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
            )}
            <StepsSwitcher
              steps={personalSheetSteps}
              activeStep={activeSection}
              onStepChange={(stepId) => handleSectionChange(stepId as PersonalSheetSectionId)}
              scrollToTopOnChange
              completedSteps={completedSections}
              errorSteps={errorSections}
              className="p-0!"
              contentClassName="surface-component-stroke!"
              hideNumbers={!isCompletionMode}>
              {renderPersonalSheetContent()}
            </StepsSwitcher>
            {isEditMode && (
              <div className="sticky bottom-0 mt-6 p-4 surface-base-fill flex items-center justify-end gap-3">
                {isCompletionMode ? (
                  <>
                    {draftSavedVisible && (
                      <Toast
                        color="positive"
                        text={t("personalSheet.completion.draftSaved")}
                        autoCloseDuration={5000}
                        onClose={() => setDraftSavedVisible(false)}
                      />
                    )}
                    <Button variant="secondary" onClick={handleSaveDraft} disabled={isSavingDraft || isCompletingSheet}>
                      {isSavingDraft ? t("personalSheet.completion.saving") : t("personalSheet.completion.save")}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleCompleteSheet}
                      disabled={!allSectionsComplete || isCompletingSheet || isSavingDraft}
                      textDuringDisabled={!allSectionsComplete ? t("personalSheet.completion.fillAllHint") : undefined}>
                      {isCompletingSheet ? t("personalSheet.completion.submitting") : t("personalSheet.completion.button")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" onClick={handleExitEditMode}>
                      {t("personalSheet.edit.cancel")}
                    </Button>
                    <Button variant="primary" onClick={handleSubmitChanges}>
                      {t("personalSheet.edit.submit")}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        );
      case "work_info": {
        const employmentProfile =
          profile?.user_type === "worker"
            ? (profile as WorkerProfile)
            : profile?.user_type === "manager" && (profile as ManagerProfile).start_date != null
              ? (profile as ManagerProfile)
              : null;
        const contract = workerOrManager.active_contract;
        return (
          <div className="radius-3xl surface-base-fill p-7">
            <h2 className="text-display-2xs content-base-primary mb-7">{t("personalSheet.tabs.workInfo")}</h2>
            <div className="flex flex-col">
              <ProfileDataRow icon={Briefcase} label={t("overview.workInfo.position")} value={jobPosition || null} />
              {employmentProfile?.start_date && (
                <ProfileDataRow icon={Calendar} label={t("overview.workInfo.startDate")} value={formatDateForDisplay(employmentProfile.start_date)} />
              )}
              {employmentProfile?.contract_type && (
                <ProfileDataRow icon={DocumentText1} label={t("overview.workInfo.contractType")} value={employmentProfile.contract_type[locale] || employmentProfile.contract_type.ru} />
              )}
              {contract && (
                <>
                  <ProfileDataRow icon={Calendar} label={t("overview.activeContract.startDate")} value={formatDateForDisplay(contract.start_date)} />
                  <ProfileDataRow icon={Calendar} label={t("overview.activeContract.endDate")} value={contract.end_date ? formatDateForDisplay(contract.end_date) : t("overview.activeContract.unlimited")} />
                  {contract.salary != null && (
                    <ProfileDataRow icon={DocumentText1} label={t("overview.activeContract.salary")} value={formatMoneyKzt(contract.salary)} />
                  )}
                </>
              )}
            </div>
          </div>
        );
      }
      case "documents":
        return (
          <div className="radius-3xl surface-base-fill p-7">
            <DocumentsSection profile={profile!} />
          </div>
        );
      default:
        return null;
    }
  }

  return (
    <>
      <title>{t("meta.profileTitle")}</title>
      <meta name="description" content={t("meta.description")} />
      <section className="flex-1 min-w-0 flex flex-col gap-6">
        <div className="p-7 radius-3xl surface-base-fill flex items-center gap-4">
          {workerOrManager.personal_sheet?.general.photo_url ? (
            <img
              src={workerOrManager.personal_sheet.general.photo_url}
              alt=""
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <Avatar name={workerOrManager.full_name} size={64} textClassName="text-display-2xs" />
          )}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <h1 className="text-heading-lg content-base-primary">{workerOrManager.full_name}</h1>
              {statusConfig && (
                <Badge
                  variant="soft"
                  color={statusConfig.color as BadgeColor}
                  text={t(statusConfig.label)}
                  icon={statusConfig.icon}
                />
              )}
            </div>
            <span className="text-body-regular-sm content-base-secondary">{workerOrManager.iin}</span>
          </div>
        </div>

        {!isCompletionMode && (
        <div className="p-6 radius-3xl surface-base-fill flex flex-wrap items-center gap-2">
          {tabs.map(function renderTab(tab) {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "px-3 py-2.5 flex justify-start items-center gap-2 radius-sm text-label-md cursor-pointer",
                  isActive ? "background-brand-fill text-white" : "surface-component-fill content-action-neutral",
                )}>
                <TabIcon size={20} color="currentColor" />
                {tab.label}
              </button>
            );
          })}
          {activeTab === "personal_sheet" && !isEditMode && hasPersonalSheet && canRequestDataChange && (
            <Button variant="secondary" size="md" onClick={handleEnterEditMode} className="ml-auto">
              <Edit2 size={16} color="currentColor" />
              {t("personalSheet.edit.button")}
            </Button>
          )}
        </div>
        )}

        {renderTabContent()}
      </section>

      {showConfirmModal && pendingChanges && workerOrManager.personal_sheet && (
        <ConfirmChangesModal
          changes={pendingChanges.changes as Record<string, Record<string, unknown> | unknown[]>}
          files={pendingChanges.files}
          personalSheet={workerOrManager.personal_sheet}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmSubmit}
          isSubmitting={isSubmitting}
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="EmployeePortal"
        />
      )}
    </>
  );
}
